import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Twitter, Linkedin, Share2 } from "lucide-react";
import { toast } from "sonner";
import { usePanelRoster } from "@/hooks/usePanelRoster";
import LivingPortrait from "@/components/panel/LivingPortrait";

const API_HTTP = process.env.REACT_APP_BACKEND_URL;

/**
 * `<PanelHomeCard />` — the flagship landing showcase for the 5 panelists.
 *
 * Sits below the compact `<PanelStrip />` teaser: the strip is the glance,
 * this is the feature. Each portrait is deliberately large (aspect-[4/5],
 * ~260-340px wide) so the character comes through, and each card carries:
 *   • Full name + role
 *   • Signature line (from persona metadata)
 *   • Deep link to /panel#{id}
 *   • Per-panelist share buttons — a targeted tweet/LinkedIn post pointing
 *     at the specific panelist anchor, which turns each face into its own
 *     shareable social-media asset.
 *
 * If Greg shares "Marcus Chen said this about my idea" on X, the link
 * lands on /panel#vc — which is now schema-visible, indexable, and
 * screenshot-friendly.
 */
export default function PanelHomeCard() {
  const { roster, loading } = usePanelRoster();
  if (loading || !roster.length) return null;

  return (
    <section
      className="border border-white/10 bg-[#080808] p-6 md:p-10"
      data-testid="panel-home-card"
    >
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div className="max-w-2xl">
          <div className="tag inline-flex mb-3">
            <span className="w-1.5 h-1.5 bg-[#E5FE40]" /> THE FULL PANEL
          </div>
          <h2 className="font-display font-black text-3xl md:text-5xl tracking-tighter leading-[0.95]">
            Five hostile experts.{" "}
            <span className="text-[#E5FE40]">Your idea.</span>
          </h2>
          <p className="mt-3 text-white/60 text-sm md:text-base max-w-xl">
            Click any face for the deep-dive — signature moves, kill rates, and
            the specific way each one will find the flaw in what you&apos;re about
            to build.
          </p>
        </div>
        <Link
          to="/panel"
          data-testid="panel-home-card-cta"
          className="btn-secondary inline-flex items-center gap-2"
        >
          Meet the panel <ArrowRight size={14} />
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        {roster.map((p) => (
          <PanelistCard key={p.id} p={p} />
        ))}
      </div>
    </section>
  );
}

function PanelistCard({ p }) {
  const [showShare, setShowShare] = useState(false);
  const anchorUrl = `https://future-venture-1.emergent.host/panel#${p.id}`;
  const shareText = `Meet ${p.name} — ${p.role}. One of the 5 hostile AI experts that stress-test your startup idea on PivotProof.`;

  const shareTwitter = () => {
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      shareText
    )}&url=${encodeURIComponent(anchorUrl)}`;
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const shareLinkedIn = () => {
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      anchorUrl
    )}`;
    window.open(u, "_blank", "noopener,noreferrer,width=600,height=500");
  };
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(anchorUrl);
      toast.success(`Link copied: ${p.name}`);
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <div
      className="group relative border border-white/10 bg-[#050505] overflow-hidden hover:border-[color:var(--accent)]/60 transition-all"
      style={{ "--accent": p.accent }}
      data-testid={`panel-home-card-${p.id}`}
    >
      <Link to={`/panel#${p.id}`} className="block" aria-label={`Meet ${p.name}`}>
        <LivingPortrait
          portrait={p.portrait}
          name={p.name}
          role={p.role}
          accent={p.accent}
          introLines={p.intro_lines}
          killRate={p.kill_rate}
          testId={`panel-home-portrait-${p.id}`}
        />
        <div className="p-3 md:p-4">
          <div
            className="text-[9px] font-mono uppercase tracking-[0.22em] mb-1"
            style={{ color: p.accent }}
          >
            # {p.mood} · Kill rate {p.kill_rate}%
          </div>
          <div className="font-display font-black text-base md:text-lg tracking-tight text-white truncate">
            {p.name}
          </div>
          <div className="text-[10px] md:text-xs font-mono text-white/50 truncate">
            {p.role}
          </div>
          {p.signature_line && (
            <p className="mt-2 text-xs text-white/70 leading-snug line-clamp-2 italic">
              &ldquo;{p.signature_line}&rdquo;
            </p>
          )}
        </div>
      </Link>

      {/* Share row — sits outside the outer Link so its buttons don't
          bubble a nav to /panel#{id} when a user just wants to share. */}
      <div className="px-3 md:px-4 pb-3 md:pb-4 pt-1 flex items-center gap-2 border-t border-white/5">
        <button
          type="button"
          onClick={() => setShowShare((s) => !s)}
          data-testid={`panel-home-share-${p.id}`}
          className="text-[10px] font-mono uppercase tracking-[0.22em] text-white/40 hover:text-[#E5FE40] inline-flex items-center gap-1"
        >
          <Share2 size={11} /> Share
        </button>
        {showShare && (
          <div className="ml-auto flex items-center gap-1" data-testid={`panel-home-share-actions-${p.id}`}>
            <button
              type="button"
              onClick={shareTwitter}
              aria-label={`Share ${p.name} on X`}
              className="p-1 text-white/50 hover:text-[#E5FE40]"
            >
              <Twitter size={12} />
            </button>
            <button
              type="button"
              onClick={shareLinkedIn}
              aria-label={`Share ${p.name} on LinkedIn`}
              className="p-1 text-white/50 hover:text-[#E5FE40]"
            >
              <Linkedin size={12} />
            </button>
            <button
              type="button"
              onClick={copyLink}
              aria-label={`Copy ${p.name} link`}
              className="text-[10px] font-mono uppercase tracking-wider text-white/50 hover:text-[#E5FE40] px-1"
            >
              copy
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
